import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CameraCapture from '../components/CameraCapture';
import DistanceCanvas, {
  DistanceCanvasHandle,
  DistanceMode,
  CalibrationPoints,
} from '../components/DistanceCanvas';
import IconButton from '../components/IconButton';
import {
  CloseIcon,
  RetakeIcon,
  SaveIcon,
  ExportIcon,
} from '../components/UIIcons';
import {
  exportCanvasJpeg,
  saveCanvasLocal,
  ExportCancelledError,
} from '../lib/exportImage';
import { haptic } from '../lib/haptics';
import { fmtMm } from '../lib/geometry';
import { buildHeightReport } from '../lib/heightReport';
import { safeTopPad, safeBottomPad, belowTopBar } from '../lib/safeArea';

type Phase = 'capture' | 'annotate';

export default function HeightPage() {
  const nav = useNavigate();
  const [phase, setPhase] = useState<Phase>('capture');
  const [frame, setFrame] = useState<HTMLCanvasElement | null>(null);
  const [mode, setMode] = useState<DistanceMode>('measure');
  const [calibration, setCalibration] = useState<CalibrationPoints | null>(null);
  const [realMm, setRealMm] = useState(1.6); // default: K-wire 1.6 mm
  const [distancePx, setDistancePx] = useState(0);
  const [distanceMm, setDistanceMm] = useState<number | null>(null);
  const [savedHint, setSavedHint] = useState<string | null>(null);
  const annotRef = useRef<DistanceCanvasHandle>(null);

  const handleCapture = (canvas: HTMLCanvasElement) => {
    void haptic('tap');
    setFrame(canvas);
    setPhase('annotate');
  };

  const retake = () => {
    setFrame(null);
    setPhase('capture');
  };

  const buildLatestReport = () => {
    if (!annotRef.current) return null;
    const annotated = annotRef.current.flatten();
    return buildHeightReport({
      annotated,
      distancePx,
      distanceMm,
      calibrationMm: calibration ? realMm : null,
      timestamp: Date.now(),
    });
  };

  const flashHint = (msg: string) => {
    setSavedHint(msg);
    setTimeout(() => setSavedHint(null), 2200);
  };

  const exportImage = async () => {
    const report = buildLatestReport();
    if (!report) return;
    try {
      await exportCanvasJpeg(report, `cadtomie-height-${Date.now()}.jpg`, 0.94);
      void haptic('success');
    } catch (err) {
      if (err instanceof ExportCancelledError) return;
      console.error('[height] Export failed', err);
      void haptic('error');
      flashHint('Export failed — try again');
    }
  };

  const saveLocal = async () => {
    const report = buildLatestReport();
    if (!report) return;
    try {
      const key = await saveCanvasLocal(report);
      flashHint(`Saved (${key})`);
    } catch (err) {
      console.error('[height] Save failed', err);
      void haptic('error');
      flashHint('Save failed — try again');
    }
  };

  const handleDistance = (px: number, mm: number | null) => {
    setDistancePx(px);
    setDistanceMm(mm);
  };

  if (phase === 'capture') {
    return <CameraCapture onCapture={handleCapture} onCancel={() => nav('/app')} />;
  }

  if (!frame) return null;

  const hudLabel =
    distanceMm !== null
      ? fmtMm(distanceMm, 1)
      : `${Math.round(distancePx)} px`;

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000' }}>
      <DistanceCanvas
        ref={annotRef}
        source={frame}
        mode={mode}
        realMm={realMm}
        onCalibrationChange={setCalibration}
        onDistanceChange={handleDistance}
      />

      {/* Top row — inline safe-area math so the bar always clears the
          Dynamic Island in standalone PWA mode. See lib/safeArea.ts. */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          paddingTop: safeTopPad(12),
          paddingBottom: 12,
          paddingLeft: 16,
          paddingRight: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          background:
            'linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0))',
          pointerEvents: 'none',
        }}
      >
        <div style={{ pointerEvents: 'auto' }}>
          <IconButton
            label="Close"
            variant="ghost"
            onClick={() => nav('/app')}
            icon={<CloseIcon />}
          />
        </div>

        <div
          aria-live="polite"
          style={{
            pointerEvents: 'auto',
            background: 'var(--surface-strong)',
            padding: '10px 18px',
            borderRadius: 999,
            border: '1px solid var(--hairline)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: 0.3,
          }}
        >
          {hudLabel}
        </div>

        <div style={{ pointerEvents: 'auto' }}>
          <IconButton
            label="Reset"
            variant="ghost"
            onClick={() => annotRef.current?.reset()}
          />
        </div>
      </div>

      {/* Mode toggle + (in calibrate mode) reference-mm input */}
      <div
        style={{
          position: 'absolute',
          top: belowTopBar(64, 12),
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          pointerEvents: 'auto',
        }}
      >
        <SegmentedControl
          value={mode}
          onChange={setMode}
          options={[
            { value: 'measure', label: 'Measure' },
            { value: 'calibrate', label: 'Calibrate' },
          ]}
        />

        {mode === 'calibrate' && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              background: 'var(--surface-strong)',
              border: '1px solid var(--hairline)',
              borderRadius: 999,
              padding: '6px 8px 6px 16px',
              backdropFilter: 'blur(18px)',
              WebkitBackdropFilter: 'blur(18px)',
              boxShadow: 'var(--shadow)',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>
              Reference
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              min="0.1"
              value={realMm}
              onChange={(e) => {
                const n = parseFloat(e.target.value);
                if (Number.isFinite(n) && n > 0) setRealMm(n);
              }}
              style={{
                width: 70,
                background: 'rgba(0,0,0,0.4)',
                border: '1px solid var(--hairline)',
                borderRadius: 12,
                color: 'var(--text)',
                padding: '8px 10px',
                fontSize: 16,
                fontWeight: 600,
                textAlign: 'right',
                fontVariantNumeric: 'tabular-nums',
              }}
            />
            <span style={{ fontSize: 13, color: 'var(--text-dim)', paddingRight: 6 }}>
              mm
            </span>
          </div>
        )}

        {mode === 'measure' && calibration && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-dim)',
              padding: '4px 12px',
              background: 'rgba(255,159,10,0.12)',
              border: '1px solid rgba(255,159,10,0.28)',
              borderRadius: 999,
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
            }}
          >
            Calibrated · {realMm.toFixed(1)} mm reference
          </div>
        )}

        {mode === 'calibrate' && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--text-dim)',
              maxWidth: 280,
              textAlign: 'center',
              padding: '0 12px',
            }}
          >
            Place the orange points on a coplanar reference of known length.
          </div>
        )}
      </div>

      {/* Bottom action bar — inline safe-area math, see lib/safeArea.ts. */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          paddingTop: 12,
          paddingBottom: safeBottomPad(16),
          paddingLeft: 16,
          paddingRight: 16,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          background:
            'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0))',
        }}
      >
        <IconButton
          label="Retake"
          variant="default"
          onClick={retake}
          icon={<RetakeIcon />}
        />
        <IconButton
          label="Save"
          variant="default"
          onClick={saveLocal}
          icon={<SaveIcon />}
        />
        <IconButton
          label="Export"
          variant="primary"
          size="lg"
          onClick={exportImage}
          icon={<ExportIcon />}
        />
      </div>

      {savedHint && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'var(--surface-strong)',
            padding: '12px 18px',
            borderRadius: 14,
            border: '1px solid var(--hairline)',
            color: 'var(--good)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            pointerEvents: 'none',
            fontSize: 15,
          }}
        >
          {savedHint}
        </div>
      )}
    </div>
  );
}

/* ─── inline segmented control (same shape as AnglePage) ─── */

interface SegOption<T extends string> {
  value: T;
  label: string;
}

function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: SegOption<T>[];
}) {
  return (
    <div
      role="tablist"
      style={{
        display: 'inline-flex',
        background: 'var(--surface-strong)',
        border: '1px solid var(--hairline)',
        borderRadius: 999,
        padding: 4,
        gap: 2,
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        boxShadow: 'var(--shadow)',
      }}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            style={{
              minHeight: 38,
              padding: '0 16px',
              borderRadius: 999,
              fontSize: 14,
              fontWeight: 600,
              color: active ? '#000' : 'var(--text)',
              background: active ? '#fff' : 'transparent',
              transition: 'background 100ms ease, color 100ms ease',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
